import { createContext, useContext, useState, useCallback, type FC, type ReactNode } from "react";
import { Alert } from "flowbite-react";
import { HiCheckCircle } from "react-icons/hi";

interface Notification {
  id: string;
  message: string;
  type: "success" | "error" | "info";
}

interface NotificationContextType {
  showNotification: (message: string, type?: "success" | "error" | "info") => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((message: string, type: "success" | "error" | "info" = "success") => {
    const id = Math.random().toString(36).substring(7);
    const newNotification: Notification = { id, message, type };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification, clearNotifications }}>
      {children}

      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2" style={{ maxWidth: "400px" }}>
        {notifications.map((notification) => (
          <Alert
            key={notification.id}
            color={notification.type === "success" ? "success" : notification.type === "error" ? "failure" : "info"}
            icon={notification.type === "success" ? HiCheckCircle : undefined}
            onDismiss={() => setNotifications((prev) => prev.filter((n) => n.id !== notification.id))}
            className="animate-slide-in-right"
          >
            <span className="font-medium">{notification.message}</span>
          </Alert>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
